import React, { useState, useEffect } from 'react';
import {
    Typography, Box, Accordion, AccordionSummary, AccordionDetails,
    Button, useTheme, useMediaQuery
} from '@mui/material';
import { ChevronDown, MessageCircle, HelpCircle, Users, CreditCard, Shield } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import './FAQ.css';

const categories = [
    { id: 'all', name: 'All', icon: HelpCircle },
    { id: 'general', name: 'General', icon: Users },
    { id: 'account', name: 'Account', icon: Users },
    { id: 'payments', name: 'Payments', icon: CreditCard },
    { id: 'privacy', name: 'Privacy & Security', icon: Shield },
];

const faqs = [
    {
        id: 1,
        question: "How does Circledin work?",
        answer: "Circledin connects individual users to form group plans on major carriers like Verizon and AT&T. We take advantage of multi-line discounts offered by these carriers to provide savings to our members, whether they're on prepaid or premium plans.",
        category: 'general',
    },
    {
        id: 2,
        question: "How do I join or create a circle?",
        answer: "You can either join an existing circle or create your own. Our platform makes it easy to find compatible circles or invite friends and family to join yours. Visit our website to get started.",
        category: 'account',
    },
    {
        id: 3,
        question: "What's the difference between an Account Owner and a Member?",
        answer: "Account Owners are verified individuals who create and manage circles. They can see usage data for their circle but not personal information like text messages. Members join existing circles to benefit from group discounts.",
        category: 'account',
    },
    {
        id: 4,
        question: "How does Circledin ensure my personal information is safe?",
        answer: "We use bank-level encryption and never share your personal details with other circle members. Your privacy and security are our top priorities. We verify all Account Owners to confirm their identity, use Stripe as our secure payment system, and implement various security measures like one-time pins and number locks.",
        category: 'privacy',
    },
    {
        id: 5,
        question: "What happens if someone in my circle doesn't pay their share?",
        answer: "Circledin guarantees all payments to the carrier. If a member is late, we cover their portion to ensure your service is never interrupted.",
        category: 'payments',
    },
];

const FAQ = () => {
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [expandedFaq, setExpandedFaq] = useState(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isExtraSmall = useMediaQuery('(max-width:480px)');
    const isVerySmall = useMediaQuery('(max-width:360px)');
    
    // For reduced motion preferences
    const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
    
    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        setPrefersReducedMotion(mediaQuery.matches);
        
        const handleChange = () => setPrefersReducedMotion(mediaQuery.matches);
        mediaQuery.addEventListener('change', handleChange);
        
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, []);

    const handleCategoryChange = (category) => {
        setSelectedCategory(category);
        setExpandedFaq(null); // Close any open FAQ when changing category
    };

    const handleFaqExpand = (faqId) => {
        setExpandedFaq(expandedFaq === faqId ? null : faqId);
    };

    const filteredFaqs = faqs.filter(faq => 
        selectedCategory === 'all' || faq.category === selectedCategory
    );
    
    // Adjust animation based on device and preferences
    const getAnimationSettings = (index) => {
        if (prefersReducedMotion) {
            return {
                initial: { opacity: 0 },
                animate: { opacity: 1 },
                exit: { opacity: 0 },
                transition: { duration: 0.1 }
            };
        }
        
        const baseDelay = isExtraSmall ? 0.05 : 0.1;
        
        return {
            initial: { opacity: 0, y: -20 },
            animate: { opacity: 1, y: 0 },
            exit: { opacity: 0, y: -20 },
            transition: { 
                duration: isExtraSmall ? 0.2 : 0.3, 
                delay: index * baseDelay 
            }
        };
    };

    return (
        <div className="faq-container">
            <Typography variant="h2" component="h2" className="faq-main-title" aria-label="Frequently Asked Questions">
                Frequently Asked Questions
            </Typography>
            
            <div className="faq-title-tabs-spacer"></div>
            
            {/* Scrollable category buttons for mobile */}
            <Box className={`faq-categories ${isMobile ? 'mobile-scroll' : ''}`} role="tablist">
                {categories.map(category => (
                    <Button
                        key={category.id}
                        startIcon={<category.icon size={isVerySmall ? 16 : isExtraSmall ? 18 : 20} />}
                        onClick={() => handleCategoryChange(category.id)}
                        className={`faq-category-button ${selectedCategory === category.id ? 'active' : ''}`}
                        aria-selected={selectedCategory === category.id}
                        role="tab"
                    >
                        {(!isMobile || isVerySmall) && 
                            <span className="faq-category-text">{category.name}</span>
                        }
                    </Button>
                ))}
            </Box>
            
            <div className="faq-categories-questions-spacer"></div>

            <div className="faq-list-container" role="region" aria-label="FAQ answers">
                <AnimatePresence>
                    {filteredFaqs.map((faq, index) => (
                        <motion.div
                            key={faq.id}
                            {...getAnimationSettings(index)}
                        >
                            <Accordion
                                expanded={expandedFaq === faq.id}
                                onChange={() => handleFaqExpand(faq.id)}
                                className={`faq-item ${expandedFaq === faq.id ? 'expanded' : ''}`}
                                disableGutters
                                TransitionProps={{ timeout: isExtraSmall ? 200 : 300 }}
                            >
                                <AccordionSummary
                                    expandIcon={<ChevronDown className="faq-chevron" />}
                                    className="faq-question"
                                    aria-controls={`faq-panel-${faq.id}`}
                                    id={`faq-header-${faq.id}`}
                                >
                                    <Typography variant="h6">{faq.question}</Typography>
                                </AccordionSummary>
                                <AccordionDetails className="faq-answer">
                                    <Typography>{faq.answer}</Typography>
                                </AccordionDetails>
                            </Accordion>
                        </motion.div>
                    ))}
                </AnimatePresence>
                
                {filteredFaqs.length === 0 && (
                    <div className="faq-no-results">
                        <Typography variant="body1">
                            No questions found in this category.
                        </Typography>
                    </div>
                )}
                
                <div className="faq-contact-wrapper">
                    <Typography variant="body1" className="faq-contact-text">
                        Still have questions?
                    </Typography>
                    <Button 
                        variant="contained" 
                        startIcon={<MessageCircle size={isExtraSmall ? 16 : 20} />}
                        className="faq-contact-button"
                    >
                        Contact Us
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default FAQ;